import React from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Stack
} from '@mui/material';
import { 
  ErrorOutline as ErrorIcon,
  Home as HomeIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  console.error('🚨 Route Error:', error);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const errorMessage = error?.message || error?.statusText || 'An unexpected error occurred';
  const isNotFound = error?.status === 404;

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3
    }}>
      <Card sx={{
        maxWidth: 500,
        width: '100%',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: isNotFound ? 'warning.light' : 'error.light',
        boxShadow: 3
      }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {/* Icon */}
          <ErrorIcon 
            color={isNotFound ? 'warning' : 'error'}
            sx={{ fontSize: 48, mb: 2 }} 
          />

          {/* Title */}
          <Typography variant="h4" color="text.primary" gutterBottom>
            {isNotFound ? '404 - Page Not Found' : 'Route Error'}
          </Typography>

          {/* Error Message */}
          <Alert severity={isNotFound ? 'warning' : 'error'} sx={{ mb: 3, textAlign: 'left' }}>
            <AlertTitle>{isNotFound ? 'Page Not Found' : 'Navigation Error'}</AlertTitle>
            <Typography variant="body2">
              {errorMessage}
            </Typography>
          </Alert>

          {/* Helpful Message */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {isNotFound 
              ? "The page you're looking for doesn't exist or may have been moved."
              : "There was a problem loading this page. Please try navigating elsewhere."
            }
          </Typography>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
            >
              Go Home
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleGoBack}
            >
              Go Back
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
